import * as React from 'react';
import { OnboardingSections } from '../../../constants';
import { OnboardingSection } from '../OnboardingSection';
import { Accordion } from '@rtkwlf/ui';
import { useSelector } from 'react-redux';
import { onboardingSelector } from '../../../reducers/onboarding';
import * as SubSections from './SubSections';
import { useOBAccordionChange } from '../../../hooks';
import { OnboardingAccordionHeader } from '../OnboardingAccordionHeader';
import { getAccordionHeader, getPluralOrSingularNoun } from '../../../utils';
import OnboardingContactList, {
  ContactListFooter,
} from './OnboardingContactList';

enum ACCORDION_KEYS {
  CONTACT_LIST = 'Contact List',
  PRIMARY_SECONDARY = 'Primary, Secondary, and Executive Sponsor Contacts',
  ESCALATION = 'Escalation Contacts',
  REPORT = 'Intelligence Reports',
  ACCESS_CONTROL = 'Access Controls',
}

const ACCORDION_ITEMS: `${ACCORDION_KEYS}`[] = [
  ACCORDION_KEYS.CONTACT_LIST,
  ACCORDION_KEYS.PRIMARY_SECONDARY,
  ACCORDION_KEYS.ESCALATION,
  ACCORDION_KEYS.REPORT,
  ACCORDION_KEYS.ACCESS_CONTROL,
];

const OnboardingContacts = () => {
  const primaryContactName = useSelector(
    onboardingSelector.getPrimaryContactName,
  );
  const contactListCompleted = useSelector(
    onboardingSelector.getContactsCompleted,
  );
  const importantContactsCompleted = useSelector(
    onboardingSelector.areImportantContactsSelected,
  );
  const escalationsCompleted = useSelector(
    onboardingSelector.areEscalationsSelected,
  );
  const reportRecipientsCompleted = useSelector(
    onboardingSelector.areReportRecipientsSelected,
  );
  const accessControlsCompleted = useSelector(
    onboardingSelector.areAccessControlsSelected,
  );

  const { collapseAll, isCollapsed, expandAll, expandedItems, handleOnChange } =
    useOBAccordionChange(ACCORDION_ITEMS);

  const contactListTitle = getAccordionHeader(
    contactListCompleted,
    expandedItems,
    ACCORDION_KEYS.CONTACT_LIST,
    getPluralOrSingularNoun(
      Object.values(useSelector(onboardingSelector.getContactsWithoutMSP))
        .length,
      'contact',
    ),
    'Contact list incomplete',
  );
  const primarySecondaryTitle = getAccordionHeader(
    importantContactsCompleted,
    expandedItems,
    ACCORDION_KEYS.PRIMARY_SECONDARY,
    'Primary contact is ' + primaryContactName,
    'Primary, secondary, and executive sponsor contacts incomplete',
  );
  const escalationTitle = getAccordionHeader(
    escalationsCompleted,
    expandedItems,
    ACCORDION_KEYS.ESCALATION,
    'Escalation contacts complete',
    'Escalation contacts incomplete',
  );
  const reportRecipientsTitle = getAccordionHeader(
    reportRecipientsCompleted,
    expandedItems,
    ACCORDION_KEYS.REPORT,
    'Report recipients complete',
    'Report recipients incomplete',
  );
  const accessControlsTitle = getAccordionHeader(
    accessControlsCompleted,
    expandedItems,
    ACCORDION_KEYS.ACCESS_CONTROL,
    'Access controls complete',
    'Access controls incomplete',
  );

  return (
    <OnboardingSection
      title={OnboardingSections.CONTACTS}
      completed={
        contactListCompleted &&
        importantContactsCompleted &&
        escalationsCompleted &&
        reportRecipientsCompleted &&
        accessControlsCompleted
      }
    >
      <OnboardingAccordionHeader
        description="This information is used to contact the correct individuals for administrative and security purposes."
        isCollapsed={isCollapsed}
        expandAll={expandAll}
        collapseAll={collapseAll}
      />
      <div className="ob-accordion-section">
        <Accordion
          itemGap={20}
          expandedItems={expandedItems}
          onChange={handleOnChange}
        >
          <Accordion.Item
            className="mr-3"
            itemKey={ACCORDION_KEYS.CONTACT_LIST}
            header={contactListTitle}
            footer={<ContactListFooter />}
          >
            <OnboardingContactList />
          </Accordion.Item>
          <Accordion.Item
            className="mr-3"
            itemKey={ACCORDION_KEYS.PRIMARY_SECONDARY}
            header={primarySecondaryTitle}
          >
            <SubSections.PrimarySecondary />
          </Accordion.Item>
          <Accordion.Item
            className="mr-3"
            itemKey={ACCORDION_KEYS.ESCALATION}
            header={escalationTitle}
          >
            <SubSections.Escalation />
          </Accordion.Item>
          <Accordion.Item
            className="mr-3"
            itemKey={ACCORDION_KEYS.REPORT}
            header={reportRecipientsTitle}
          >
            <SubSections.Report />
          </Accordion.Item>
          <Accordion.Item
            className="mr-3"
            itemKey={ACCORDION_KEYS.ACCESS_CONTROL}
            header={accessControlsTitle}
          >
            <SubSections.AccessControl />
          </Accordion.Item>
        </Accordion>
      </div>
    </OnboardingSection>
  );
};

export default OnboardingContacts;
